import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Brain, PlugsConnected, Trash, Check } from "@phosphor-icons/react";

const PROVIDERS = [
    { value: "emergent", title: "Emergent (base)", desc: "Clave universal incluida. Sin configuración — es el modo por defecto del sistema." },
    { value: "anthropic", title: "Mi Claude (Anthropic)", desc: "Usa tu propia API key de Anthropic. Obtén la key en console.anthropic.com." },
    { value: "openai", title: "Mi ChatGPT (OpenAI)", desc: "Usa tu propia API key de OpenAI. Obtén la key en platform.openai.com." },
];

export const LLMSettings = () => {
    const [cfg, setCfg] = useState(null);
    const [provider, setProvider] = useState("emergent");
    const [models, setModels] = useState({ emergent_model: "", anthropic_model: "", openai_model: "" });
    const [anthropicKey, setAnthropicKey] = useState("");
    const [openaiKey, setOpenaiKey] = useState("");
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        const { data } = await api.get("/settings/llm");
        setCfg(data);
        setProvider(data.provider);
        setModels({
            emergent_model: data.emergent_model,
            anthropic_model: data.anthropic_model,
            openai_model: data.openai_model,
        });
    };
    useEffect(() => { load().catch(() => {}); }, []);

    const save = async () => {
        setSaving(true);
        setTestResult(null);
        try {
            const body = { provider, ...models };
            if (anthropicKey.trim()) body.anthropic_api_key = anthropicKey.trim();
            if (openaiKey.trim()) body.openai_api_key = openaiKey.trim();
            const { data } = await api.put("/settings/llm", body);
            setCfg(data);
            setAnthropicKey(""); setOpenaiKey("");
            toast.success("Configuración de IA guardada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
        } finally { setSaving(false); }
    };

    const clearKey = async (field) => {
        try {
            const { data } = await api.put("/settings/llm", { [field]: "" });
            setCfg(data);
            toast.success("Key eliminada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo eliminar (¿está en uso por el proveedor activo?)");
        }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/settings/llm/test");
            setTestResult(data);
            if (data.ok) toast.success(`Conexión OK con ${data.model}`);
            else toast.error("La prueba falló — revisa la configuración");
        } catch (e) {
            setTestResult({ ok: false, error: e.response?.data?.detail || "Error" });
            toast.error(e.response?.data?.detail || "Error en la prueba");
        } finally { setTesting(false); }
    };

    if (!cfg) return <div className="border hairline bg-white p-6 text-sm text-[var(--ink-3)]">Cargando configuración de IA…</div>;

    const modelField = {
        emergent: "emergent_model",
        anthropic: "anthropic_model",
        openai: "openai_model",
    }[provider];

    return (
        <div className="border hairline bg-white p-6 space-y-5" data-testid="llm-settings">
            <p className="text-sm text-[var(--ink-2)]">
                Por defecto el sistema usa <strong>Emergent</strong> (clave universal). Opcionalmente puedes
                conmutar a <strong>tu propio Claude o ChatGPT</strong> con tus API keys y elegir el modelo en
                cada momento. La potencia se mantiene: si tu proveedor falla, el sistema recurre automáticamente
                al otro proveedor configurado o a Emergent como red de seguridad.
            </p>

            {/* Selector de proveedor */}
            <div className="grid sm:grid-cols-3 gap-2">
                {PROVIDERS.map((p) => (
                    <button key={p.value} onClick={() => setProvider(p.value)}
                        className={`text-left border p-4 transition-colors ${provider === p.value ? "border-[var(--sage)] bg-[var(--cream)]" : "hairline hover:bg-[var(--cream)]"}`}
                        data-testid={`llm-provider-${p.value}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{p.title}</span>
                            {provider === p.value && <Check size={14} className="text-[var(--sage)]" weight="bold"/>}
                        </div>
                        <p className="text-xs text-[var(--ink-3)] mt-1.5 leading-relaxed">{p.desc}</p>
                    </button>
                ))}
            </div>

            {/* Modelo del proveedor activo */}
            <div>
                <label className="label-eyebrow block mb-2">Modelo activo ({provider})</label>
                <input className="input-base mono" list={`models-${provider}`}
                    value={models[modelField] || ""}
                    onChange={(e) => setModels((m) => ({ ...m, [modelField]: e.target.value }))}
                    placeholder="Escribe o elige un modelo"
                    data-testid="llm-model-input"/>
                <datalist id={`models-${provider}`}>
                    {(cfg.available_models?.[provider] || []).map((m) => <option key={m} value={m}/>)}
                </datalist>
                <p className="text-xs text-[var(--ink-3)] mt-1">Puedes escribir cualquier modelo del proveedor, no solo los sugeridos.</p>
            </div>

            {/* Keys propias */}
            <div className="border hairline bg-[var(--cream)] p-3 text-xs text-[var(--ink-2)] leading-relaxed">
                <strong className="text-[var(--sage)]">Red de seguridad automática:</strong> cualquier API key
                que guardes aquí (Anthropic / OpenAI) se usará como respaldo real si el proveedor seleccionado
                falla transitoriamente. Así nunca te quedarás sin generar fórmulas aunque Emergent tenga un hipo.
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <label className="label-eyebrow block mb-2">
                        API key Anthropic {cfg.has_anthropic_key && <span className="text-[var(--sage)]">· guardada ({cfg.anthropic_key_masked})</span>}
                    </label>
                    <div className="flex gap-1.5">
                        <input type="password" className="input-base mono" value={anthropicKey}
                            onChange={(e) => setAnthropicKey(e.target.value)}
                            placeholder={cfg.has_anthropic_key ? "•••• (escribe para reemplazar)" : "sk-ant-…"}
                            data-testid="llm-anthropic-key-input"/>
                        {cfg.has_anthropic_key && (
                            <button onClick={() => clearKey("anthropic_api_key")} title="Borrar key"
                                className="px-3 border hairline hover:bg-[var(--cream)] text-[var(--terra-2)]"
                                data-testid="llm-clear-anthropic-key">
                                <Trash size={14}/>
                            </button>
                        )}
                    </div>
                </div>
                <div>
                    <label className="label-eyebrow block mb-2">
                        API key OpenAI {cfg.has_openai_key && <span className="text-[var(--sage)]">· guardada ({cfg.openai_key_masked})</span>}
                    </label>
                    <div className="flex gap-1.5">
                        <input type="password" className="input-base mono" value={openaiKey}
                            onChange={(e) => setOpenaiKey(e.target.value)}
                            placeholder={cfg.has_openai_key ? "•••• (escribe para reemplazar)" : "sk-…"}
                            data-testid="llm-openai-key-input"/>
                        {cfg.has_openai_key && (
                            <button onClick={() => clearKey("openai_api_key")} title="Borrar key"
                                className="px-3 border hairline hover:bg-[var(--cream)] text-[var(--terra-2)]"
                                data-testid="llm-clear-openai-key">
                                <Trash size={14}/>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <button onClick={save} disabled={saving} className="btn-primary" data-testid="llm-save-button">
                    {saving ? "Guardando…" : "Guardar configuración IA"}
                </button>
                <button onClick={test} disabled={testing}
                    className="px-4 py-2.5 border hairline bg-white text-sm hover:bg-[var(--cream)] inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="llm-test-button">
                    <PlugsConnected size={14}/> {testing ? "Probando…" : "Probar conexión"}
                </button>
            </div>

            {testResult && (
                <div className={`text-sm border hairline p-3 ${testResult.ok ? "bg-[var(--cream)]" : "bg-[var(--cream)] text-[var(--terra-2)]"}`} data-testid="llm-test-result">
                    {testResult.ok
                        ? <>✓ Conexión correcta con <code className="mono">{testResult.model}</code> — respuesta: "{testResult.response}"</>
                        : <>✗ Fallo {testResult.model ? <>con <code className="mono">{testResult.model}</code></> : null}: {testResult.error}</>}
                </div>
            )}
        </div>
    );
};
